import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PurpleNavBar } from '../components/PurpleNavBar';
import { UploadDocumentDrawer } from '../components/UploadDocumentDrawer';
import { EditProfileDrawer } from '../components/EditProfileDrawer';
import { COLORS } from '../../../core/constants/colors';
import { Company, Query, Document } from '../types';
import { api } from '../../../core/utils/apiClient';

import { DocumentViewerModal } from '../../admin/documents/components/DocumentViewerModal';

export const PartnerProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [company, setCompany] = useState<Company | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [queryText, setQueryText] = useState('');
  const [topK, setTopK] = useState(5);
  const [includeSources, setIncludeSources] = useState(true);
  const [isQueryLoading, setIsQueryLoading] = useState(false);
  const [queryHistory, setQueryHistory] = useState<Query[]>([]);
  const [currentQuery, setCurrentQuery] = useState<Query | null>(null);
  const [error, setError] = useState('');
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);
  const [showUploadDrawer, setShowUploadDrawer] = useState(false);
  const [showEditDrawer, setShowEditDrawer] = useState(false);

  useEffect(() => {
    loadPartnerCompany();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPartnerCompany = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Get the partner's companies
      const response = await api.get('/api/v1/companies/my-companies');

      if (response.status === 404) {
        // Partner has no company yet
        setCompany(null);
        setError('No company found. Please contact support.');
        return;
      }

      if (response.ok) {
        const data = await response.json();
        // Handle list response - take first company
        const companiesList = Array.isArray(data) ? data : (data.companies || []);
        const partnerCompany = companiesList.length > 0 ? companiesList[0] : null;

        if (partnerCompany && typeof partnerCompany === 'object') {
          setCompany(partnerCompany);

          // Load query history and documents for this company
          loadQueryHistory(partnerCompany.id);
          loadDocuments(partnerCompany.id);
        } else {
          setError('No company found. Please contact support.');
        }
      } else {
        setError('Failed to load company data');
      }
    } catch (err: any) {
      console.error('Error loading partner company:', err);
      setError(err.message || 'Failed to load company data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadQueryHistory = async (companyId: number) => {
    try {
      const historyResponse = await api.get(`/api/v1/llm/query/history?company_id=${companyId}&limit=10`);

      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        const companyQueries = (Array.isArray(historyData) ? historyData : historyData.queries || [])
          .filter((q: Query) => q.company_id === companyId)
          .map((q: Query) => normalizeQuery(q));
        setQueryHistory(companyQueries);
      }
    } catch (error) {
      console.error('Error loading query history:', error);
    }
  };

  const loadDocuments = async (companyId: number) => {
    try {
      const docsResponse = await api.get(`/api/v1/documents/companies/${companyId}/documents`);

      if (docsResponse.ok) {
        const docsData = await docsResponse.json();
        setDocuments(docsData.documents || []);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  };

  const handleDocumentClick = (doc: Document) => {
    const adminDoc = {
      id: doc.id,
      file_name: doc.file_name,
      original_file_name: doc.original_file_name,
      file_size: doc.file_size,
      file_type: doc.file_type,
      mime_type: doc.mime_type,
      s3_bucket: doc.s3_bucket,
      s3_key: doc.s3_key,
      company_id: doc.company_id,
      status: doc.status,
      document_type: doc.document_type,
      tags: doc.tags,
      page_count: doc.page_count,
      word_count: doc.word_count,
      created_at: doc.created_at,
      updated_at: doc.updated_at,
      created_by: doc.created_by,
      company_name: company?.name,
    };
    setSelectedDocument(adminDoc as any);
    setIsDocumentViewerOpen(true);
  };

  const handleUploadSuccess = () => {
    if (company) {
      loadDocuments(company.id);
      setShowUploadDrawer(false);
    }
  };

  const normalizeQuery = useCallback(
    (raw: any, fallbackQueryText?: string): Query => {
      const normalizedAnswer = raw?.answer ?? raw?.response_text ?? raw?.answer_preview ?? '';
      const timestamp = raw?.created_at ?? new Date().toISOString();
      return {
        id: raw?.id ?? raw?.query_id ?? Date.now(),
        query_id: raw?.query_id,
        query_text: raw?.query_text ?? fallbackQueryText ?? '',
        answer: raw?.answer,
        answer_preview: raw?.answer_preview ?? normalizedAnswer,
        response_text: raw?.response_text ?? normalizedAnswer,
        status: raw?.status ?? 'success',
        user_id: raw?.user_id,
        company_id: raw?.company_id ?? company?.id ?? null,
        response_time_ms: raw?.response_time_ms,
        tokens_used: raw?.tokens_used,
        prompt_tokens: raw?.prompt_tokens,
        completion_tokens: raw?.completion_tokens,
        top_k_used: raw?.top_k_used,
        chunks_retrieved: raw?.chunks_retrieved,
        source_documents: raw?.source_documents,
        sources: raw?.sources,
        error_message: raw?.error_message,
        error_type: raw?.error_type,
        model_used: raw?.model_used,
        embedding_model_used: raw?.embedding_model_used,
        created_at: timestamp,
        completed_at: raw?.completed_at,
      };
    },
    [company]
  );

  const handleSave = async (updateData: Partial<Company>) => {
    if (!company) return;

    setIsSaving(true);
    setError('');
    try {
      const response = await api.put(`/api/v1/companies/${company.id}`, updateData);

      if (response.ok) {
        const updatedCompany = await response.json();
        setCompany(updatedCompany);
        setShowEditDrawer(false);
        // Reload company data to get updated profile completeness score
        await loadPartnerCompany();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to update company');
      }
    } catch (err: any) {
      console.error('Error updating company:', err);
      setError(err.message || 'Failed to update company');
    } finally {
      setIsSaving(false);
    }
  };

  const handleQuerySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!queryText.trim() || !company) return;

    setIsQueryLoading(true);

    try {
      const response = await api.post('/api/v1/llm/query', {
        company_id: company.id,
        query_text: queryText,
        top_k: topK,
        include_sources: includeSources,
      });

      if (response.ok) {
        const data = await response.json();
        const normalizedQuery = normalizeQuery(data, queryText);
        setCurrentQuery(normalizedQuery);
        setQueryHistory((prev) => [normalizedQuery, ...prev]);
        setQueryText('');
      }
    } catch (error) {
      console.error('Error executing query:', error);
    } finally {
      setIsQueryLoading(false);
    }
  };

  const getCompanyInitial = (name: string) => {
    return name?.charAt(0).toUpperCase() || 'C';
  };

  const getInitialColor = (name: string) => {
    const colors = [COLORS.user.primary, COLORS.extended.orange, COLORS.extended.azure, COLORS.primary.deepSoftLime];
    const index = name?.charCodeAt(0) % colors.length || 0;
    return colors[index];
  };

  const suggestedQuestions = [
    "What are the key financial metrics?",
    "Summarize latest reports",
    "What are the main risks?",
    "Show revenue trends",
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F5F5F5', fontFamily: 'DM Sans, sans-serif' }}>
        <PurpleNavBar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div
              className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent mb-4"
              style={{ borderColor: COLORS.user.primary, borderTopColor: 'transparent' }}
            />
            <p style={{ color: COLORS.primary.persianIndigo }}>Loading company profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F5F5F5', fontFamily: 'DM Sans, sans-serif' }}>
        <PurpleNavBar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <p className="text-lg font-semibold mb-2" style={{ color: COLORS.extended.redSalsa }}>
              {error || 'Company not found'}
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-2 rounded-lg font-medium text-white mt-4"
              style={{ backgroundColor: COLORS.user.primary }}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F5F5', fontFamily: 'DM Sans, sans-serif' }}>
      {/* Azure Header */}
      <div style={{ backgroundColor: COLORS.user.primary }} className="relative overflow-hidden">
        {/* Large Tilted Send Icon Background */}
        <div
          className="absolute right-0 top-0 bottom-0 flex items-center pointer-events-none"
          style={{ opacity: 0.18 }}
        >
          <img
            src="/new_logo_white_P.png"
            alt="Portfolio Indexing System"
            className="h-96 opacity-50"
          />
        </div>
        <PurpleNavBar />

        <div className="max-w-[1400px] mx-auto px-8 py-6 relative z-10">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-2 text-sm text-white mb-6" style={{ opacity: 0.9 }}>
            <button onClick={() => navigate('/dashboard')} className="hover:underline">
              Dashboard
            </button>
            <span>›</span>
            <span>Partner Profile</span>
          </div>

          {/* Company Header */}
          <div className="flex items-start justify-between gap-8">
            <div className="flex items-start gap-4 flex-1">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold"
                style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
              >
                {getCompanyInitial(company.name)}
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-white mb-2">
                  {company.name}
                </h1>
                <div className="space-y-2">
                  <div className="flex items-center gap-4 text-white text-sm" style={{ opacity: 0.9 }}>
                    {company.founding_year && (
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                        <span>Founded {company.founding_year}</span>
                      </div>
                    )}
                    {company.industry && (
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                          <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
                        </svg>
                        <span>{company.industry}</span>
                      </div>
                    )}
                    {company.location && (
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                        <span>{company.location}</span>
                      </div>
                    )}
                  </div>
                  {company.website && (
                    <div className="flex items-center gap-1 text-white text-sm" style={{ opacity: 0.9 }}>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" clipRule="evenodd" />
                      </svg>
                      <a 
                        href={company.website} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="hover:underline"
                      >
                        {company.website}
                      </a>
                    </div>
                  )}
                </div>

                {/* Tags */}
                <div className="flex items-center gap-2 mt-3">
                  {company.industry && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#FFFFFF' }}>
                      {company.industry}
                    </span>
                  )}
                  {company.location && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#FFFFFF' }}>
                      {company.location}
                    </span>
                  )}
                  <span
                    className="px-3 py-1 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: company.is_active ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                      color: '#FFFFFF'
                    }}
                  >
                    {company.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            {/* Right Side - Scores and Actions */}
            <div className="flex flex-col items-end gap-4">
              {/* Action Buttons */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="px-6 py-2 rounded-lg font-medium text-white hover:bg-white hover:bg-opacity-20 transition-all"
                  style={{ border: '1px solid rgba(255,255,255,0.3)' }}
                >
                  ← Back
                </button>
                <button
                  onClick={() => setShowEditDrawer(true)}
                  className="px-6 py-2 rounded-lg font-medium text-white transition-all"
                  style={{ backgroundColor: COLORS.user.queryButton }}
                >
                  Edit Profile
                </button>
              </div>

              {/* Scores */}
              <div className="flex gap-4">
                {/* Profile Completeness */}
                {company.profile_completeness_score !== undefined && (
                  <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-4 min-w-[180px]">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs text-white font-medium" style={{ opacity: 0.9 }}>
                        Profile
                      </p>
                      <p className="text-lg font-bold text-white">
                        {company.profile_completeness_score}%
                      </p>
                    </div>
                    <div className="w-full bg-white bg-opacity-20 rounded-full h-2">
                      <div
                        className="h-2 rounded-full transition-all"
                        style={{
                          width: `${company.profile_completeness_score}%`,
                          backgroundColor: '#FFFFFF',
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Enrichment Score */}
                {company.enrichment_score !== undefined && (
                  <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-4 min-w-[180px]">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs text-white font-medium" style={{ opacity: 0.9 }}>
                        Enrichment
                      </p>
                      <p className="text-lg font-bold text-white">
                        {Math.round(company.enrichment_score)}%
                      </p>
                    </div>
                    <div className="w-full bg-white bg-opacity-20 rounded-full h-2">
                      <div
                        className="h-2 rounded-full transition-all"
                        style={{
                          width: `${company.enrichment_score}%`,
                          backgroundColor: '#FFFFFF',
                        }}
                      />
                    </div>
                    {company.last_enrichment_at && (
                      <p className="text-xs text-white mt-2" style={{ opacity: 0.7 }}>
                        Last: {new Date(company.last_enrichment_at || '').toLocaleDateString()}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-8 py-8">
        {error && (
          <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: '#FEE2E2' }}>
            <p className="text-sm" style={{ color: COLORS.extended.redSalsa }}>{error}</p>
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Company Info */}
          <div className="lg:col-span-2 space-y-6">

            {/* Company Overview Card */}
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: getInitialColor(company.name) + '20' }}
                >
                  <svg className="w-6 h-6" style={{ color: getInitialColor(company.name) }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold" style={{ color: '#1F2937' }}>
                    Company Overview
                  </h2>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-sm mb-1" style={{ color: '#6B7280' }}>Industry</p>
                  <p className="font-medium" style={{ color: '#1F2937' }}>{company.industry || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm mb-1" style={{ color: '#6B7280' }}>Location</p>
                  <p className="font-medium" style={{ color: '#1F2937' }}>{company.location || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm mb-1" style={{ color: '#6B7280' }}>Employees</p>
                  <p className="font-medium" style={{ color: '#1F2937' }}>{company.employees || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm mb-1" style={{ color: '#6B7280' }}>Founding Year</p>
                  <p className="font-medium" style={{ color: '#1F2937' }}>{company.founding_year || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm mb-1" style={{ color: '#6B7280' }}>Created</p>
                  <p className="font-medium" style={{ color: '#1F2937' }}>{new Date(company.created_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-sm mb-1" style={{ color: '#6B7280' }}>Last Updated</p>
                  <p className="font-medium" style={{ color: '#1F2937' }}>{company.updated_at ? new Date(company.updated_at).toLocaleDateString() : 'N/A'}</p>
                </div>
                {company.description && (
                  <div className="col-span-2">
                    <p className="text-sm mb-1" style={{ color: '#6B7280' }}>Description</p>
                    <p className="font-medium" style={{ color: '#1F2937' }}>{company.description}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Documents Section */}
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold" style={{ color: '#1F2937' }}>
                  Company Documents
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowUploadDrawer(true)}
                    className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
                    style={{ backgroundColor: COLORS.user.queryButton, color: 'white' }}
                  >
                    Upload
                  </button>
                  <button
                    onClick={() => navigate(`/dashboard/documents?company=${company.id}`)}
                    className="text-sm font-medium hover:underline"
                    style={{ color: COLORS.user.primary }}
                  >
                    View all →
                  </button>
                </div>
              </div>

              {documents.length > 0 ? (
                <div className="space-y-3">
                  {documents.slice(0, 5).map((doc) => (
                    <div
                      key={doc.id}
                      onClick={() => handleDocumentClick(doc)}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                      style={{ border: '1px solid #E5E7EB' }}
                    >
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: '#FFF3E0' }}
                      >
                        <svg className="w-5 h-5" style={{ color: '#FF5E2E' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate" style={{ color: '#1F2937' }}>
                          {doc.original_file_name}
                        </p>
                        <p className="text-xs" style={{ color: '#6B7280' }}>
                          {new Date(doc.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <span
                        className="px-2 py-1 rounded text-xs font-medium"
                        style={{
                          backgroundColor: doc.status === 'completed' ? '#D1FAE5' : '#FEF3C7',
                          color: doc.status === 'completed' ? '#059669' : '#D97706'
                        }}
                      >
                        {doc.status}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8" style={{ color: '#9CA3AF' }}>
                  <svg className="w-12 h-12 mx-auto mb-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                  </svg>
                  <p>No documents uploaded yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Query ChatBot */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg p-6 shadow-sm sticky top-8">
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: COLORS.user.queryButtonLight }}
                >
                  <svg className="w-5 h-5" style={{ color: COLORS.user.queryButton }} fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 3.5a1.5 1.5 0 013 0V4a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-.5a1.5 1.5 0 000 3h.5a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-.5a1.5 1.5 0 00-3 0v.5a1 1 0 01-1 1H6a1 1 0 01-1-1v-3a1 1 0 00-1-1h-.5a1.5 1.5 0 010-3H4a1 1 0 001-1V6a1 1 0 011-1h3a1 1 0 001-1v-.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold" style={{ color: '#1F2937' }}>
                  Company AI Assistant
                </h3>
              </div>

              <p className="text-sm mb-4" style={{ color: '#6B7280' }}>
                Ask questions specifically about <span className="font-semibold">{company.name}</span> using our AI assistant.
              </p>

              {/* Query Settings */}
              <div className="space-y-3 mb-4 p-3 rounded-lg" style={{ backgroundColor: '#F9FAFB' }}>
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium" style={{ color: '#6B7280' }}>Top K Resources</label>
                  <input
                    type="number"
                    value={topK}
                    onChange={(e) => setTopK(parseInt(e.target.value))}
                    min="1"
                    max="10"
                    className="w-16 px-2 py-1 text-xs rounded border focus:outline-none focus:ring-2"
                    style={{ borderColor: '#E5E7EB' }}
                    onFocus={(e) => {
                      e.target.style.borderColor = COLORS.user.queryButton;
                      e.target.style.boxShadow = `0 0 0 2px ${COLORS.user.queryButton}40`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#E5E7EB';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium" style={{ color: '#6B7280' }}>Include Sources</label>
                  <button
                    onClick={() => setIncludeSources(!includeSources)}
                    className={`w-12 h-6 rounded-full transition-all ${includeSources ? '' : 'bg-gray-300'}`}
                    style={{ backgroundColor: includeSources ? COLORS.user.queryButton : undefined }}
                  >
                    <div className={`w-5 h-5 rounded-full bg-white transition-transform ${includeSources ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
              </div>

              {/* Current Query Response */}
              {currentQuery && (currentQuery.answer_preview || currentQuery.response_text) && (
                <div className="mb-4 p-4 rounded-lg border-2" style={{ borderColor: COLORS.user.primary }}>
                  <div className="flex items-start gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0" style={{ backgroundColor: COLORS.user.primary }}>
                      A
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-bold mb-1" style={{ color: '#1F2937' }}>
                        {currentQuery.query_text}
                      </p>
                      <p className="text-xs leading-relaxed" style={{ color: '#374151' }}>
                        {currentQuery.answer_preview || currentQuery.response_text}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <span
                          className="px-2 py-0.5 rounded text-xs font-medium"
                          style={{
                            backgroundColor: currentQuery.status === 'success' ? '#D1FAE5' : '#FEE2E2',
                            color: currentQuery.status === 'success' ? '#059669' : '#DC2626'
                          }}
                        >
                          {currentQuery.status}
                        </span>
                        {currentQuery.response_time_ms && (
                          <span className="text-xs" style={{ color: '#9CA3AF' }}>
                            {Math.round(currentQuery.response_time_ms)}ms
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Query History */}
              {queryHistory.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium" style={{ color: '#6B7280' }}>Recent queries:</p>
                    <button
                      onClick={() => navigate('/dashboard/history')}
                      className="text-xs font-medium hover:underline"
                      style={{ color: COLORS.user.primary }}
                    >
                      View all →
                    </button>
                  </div>
                  <div className="max-h-64 overflow-y-auto space-y-3">
                    {queryHistory.slice(0, 3).map((query) => (
                      <div
                        key={query.id}
                        onClick={() => setCurrentQuery(query)}
                        className="p-3 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                        style={{ backgroundColor: '#F9FAFB' }}
                      >
                        <div className="flex items-start gap-2 mb-2">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0" style={{ backgroundColor: COLORS.user.primary }}>
                            Q
                          </div>
                          <p className="text-xs font-medium flex-1" style={{ color: '#1F2937' }}>
                            {query.query_text}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 pl-8 mt-1">
                          <span
                            className="px-2 py-0.5 rounded text-xs font-medium"
                            style={{
                              backgroundColor: query.status === 'success' ? '#D1FAE5' : '#FEE2E2',
                              color: query.status === 'success' ? '#059669' : '#DC2626'
                            }}
                          >
                            {query.status}
                          </span>
                          <p className="text-xs" style={{ color: '#9CA3AF' }}>
                            {query.created_at ? new Date(query.created_at).toLocaleTimeString() : '—'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Suggested Questions */}
              <div className="mb-4">
                <p className="text-xs font-medium mb-2" style={{ color: '#6B7280' }}>Most asked questions:</p>
                <div className="space-y-2">
                  {suggestedQuestions.map((question, index) => (
                    <button
                      key={index}
                      onClick={() => setQueryText(question)}
                      className="w-full text-left text-xs p-2 rounded hover:bg-gray-50 transition-colors flex items-center gap-2"
                      style={{ color: COLORS.user.queryButton }}
                    >
                      <span>→</span>
                      <span>{question}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Query Input */}
              <form onSubmit={handleQuerySubmit} className="space-y-3">
                <textarea
                  value={queryText}
                  onChange={(e) => setQueryText(e.target.value)}
                  placeholder="Enter your question"
                  rows={3}
                  className="w-full px-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 resize-none"
                  style={{ borderColor: '#E5E7EB' }}
                  onFocus={(e) => {
                    e.target.style.borderColor = COLORS.user.queryButton;
                    e.target.style.boxShadow = `0 0 0 2px ${COLORS.user.queryButton}40`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#E5E7EB';
                    e.target.style.boxShadow = 'none';
                  }}
                  disabled={isQueryLoading}
                />
                <button
                  type="submit"
                  disabled={isQueryLoading || !queryText.trim()}
                  className="w-full py-3 rounded-lg font-semibold text-white transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={{ backgroundColor: COLORS.user.queryButton }}
                >
                  {isQueryLoading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span className="text-sm">Thinking...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <span className="text-sm">Send</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Document Drawer */}
      {company && (
        <UploadDocumentDrawer
          isOpen={showUploadDrawer}
          onClose={() => setShowUploadDrawer(false)}
          companyId={company.id}
          onUploadComplete={handleUploadSuccess}
        />
      )}

      {/* Edit Profile Drawer */}
      {company && (
        <EditProfileDrawer
          isOpen={showEditDrawer}
          onClose={() => setShowEditDrawer(false)}
          company={company}
          onSave={handleSave}
          isSaving={isSaving}
        />
      )}

      {/* Document Viewer Modal */}
      {isDocumentViewerOpen && selectedDocument && (
        <DocumentViewerModal
          document={selectedDocument as any}
          isOpen={isDocumentViewerOpen}
          onClose={() => {
            setIsDocumentViewerOpen(false);
            setSelectedDocument(null);
          }}
        />
      )}
    </div>
  );
};
